import React from 'react';
import { FiList } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const SequencesLesson = () => {
  const sections = [
    {
      id: 'arithmetic-geometric',
      title: 'Arithmetic and Geometric Sequences',
      content: {
        introduction:
          'Sequence questions reward pattern recognition. First ask: is the sequence adding the same amount, multiplying by the same amount, or following a custom rule?',
        concepts: [
          {
            title: 'Arithmetic Sequences',
            prerequisiteNote:
              'Use this when each term increases or decreases by the same amount.',
            vocabulary: ['term', 'common difference', 'nth term'],
            explanation:
              'Arithmetic sequence formula: §a_n = a_1 + (n-1)d§, where §d§ is the common difference.',
            microExample: {
              problem: 'Find the 5th term: 3, 7, 11, ...',
              solution: ['Common difference: §d=4§', '§a_5 = 3 + 4(4) = 19§'],
            },
            greTrap:
              'The nth term uses §n-1§ jumps from the first term, not n jumps.',
            checkQuestion: 'How many jumps are there from term 1 to term 10?',
            example: {
              problem:
                'In an arithmetic sequence, §a_1 = 8§ and §d = 5§. What is §a_{12}§?',
              solution: [
                'Use §a_n = a_1 + (n-1)d§',
                '§a_{12} = 8 + (12-1)5§',
                '§a_{12} = 8 + 55 = 63§',
              ],
            },
          },
          {
            title: 'Geometric Sequences',
            prerequisiteNote:
              'Use this when each term is multiplied by the same number.',
            vocabulary: ['common ratio', 'power', 'exponential growth'],
            explanation:
              'Geometric sequence formula: §a_n = a_1r^{n-1}§, where §r§ is the common ratio.',
            microExample: {
              problem: 'Find the 4th term: 2, 6, 18, ...',
              solution: ['Common ratio: §r=3§', '4th term: §18×3=54§'],
            },
            greTrap:
              'Do not use the arithmetic formula when terms multiply instead of add.',
            checkQuestion: 'In 5, 10, 20, 40, what is the common ratio?',
            example: {
              problem:
                'In a geometric sequence, §a_1=3§ and §r=2§. What is §a_6§?',
              solution: [
                'Use §a_n = a_1r^{n-1}§',
                '§a_6 = 3 × 2^5§',
                '§a_6 = 3 × 32 = 96§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'recursive-series',
      title: 'Recursive Rules and Series',
      content: {
        introduction:
          'Some GRE sequences give a rule using the previous term. Others ask for a sum. Slow down and identify whether you need a term or a total.',
        concepts: [
          {
            title: 'Recursive Sequences',
            prerequisiteNote:
              'A recursive rule tells you how to get the next term from earlier terms.',
            vocabulary: ['recursive', 'previous term', 'initial value'],
            explanation:
              'For recursive rules, start from the given first term and iterate carefully. Write each term on a new line to avoid skipping.',
            microExample: {
              problem: 'If §a_1=2§ and §a_n=a_{n-1}+3§, find §a_4§.',
              solution: ['§a_2=5§', '§a_3=8§', '§a_4=11§'],
            },
            greTrap:
              'Do not plug n into a recursive rule unless the rule explicitly gives a formula in n.',
            checkQuestion:
              'If a rule uses §a_{n-1}§, is it explicit or recursive?',
            example: {
              problem:
                'If §a_1=1§ and §a_n=2a_{n-1}+1§, what is §a_4§?',
              solution: [
                '§a_2 = 2(1)+1 = 3§',
                '§a_3 = 2(3)+1 = 7§',
                '§a_4 = 2(7)+1 = 15§',
              ],
            },
          },
          {
            title: 'Arithmetic Series',
            prerequisiteNote:
              'A series asks for a sum, not just a term.',
            vocabulary: ['series', 'sum', 'average of first and last'],
            explanation:
              'Sum of arithmetic series: §S_n = n(a_1+a_n)/2§. It works because pairs from the ends have the same total.',
            microExample: {
              problem: 'Find §1+2+3+4+5§.',
              solution: ['There are 5 terms.', '§S = 5(1+5)/2 = 15§'],
            },
            greTrap:
              'Make sure n is the number of terms, not necessarily the last number.',
            checkQuestion: 'How many terms are in 4, 7, 10, 13?',
            example: {
              problem:
                'Find the sum of the first 20 positive even integers.',
              solution: [
                'First term: 2, last term: 40, number of terms: 20',
                '§S = 20(2+40)/2§',
                '§S = 10 × 42 = 420§',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'untimed',
      question: 'What is the 8th term of 5, 8, 11, ...?',
      options: ['23', '24', '26', '29'],
      correct: 2,
      explanation: 'The common difference is 3. §a_8 = 5 + 7×3 = 26§.',
    },
    {
      type: 'untimed',
      question: 'What is the common ratio of 4, 12, 36, ...?',
      options: ['2', '3', '4', '8'],
      correct: 1,
      explanation: 'Each term is multiplied by 3.',
    },
    {
      type: 'untimed',
      question: 'How many jumps are there from term 1 to term 10?',
      options: ['8', '9', '10', '11'],
      correct: 1,
      explanation: 'From term 1 to term n there are §n-1§ jumps, so 9.',
    },
    {
      type: 'untimed',
      question: 'In an arithmetic sequence, §a_1 = 8§ and §d = 5§. What is §a_4§?',
      options: ['18', '23', '28', '13'],
      correct: 1,
      explanation: '§a_4 = 8 + 3×5 = 23§.',
    },
    {
      type: 'untimed',
      question: 'What is the 4th term of 2, 6, 18, ...?',
      options: ['36', '54', '72', '24'],
      correct: 1,
      explanation: 'The common ratio is 3, so the 4th term is §18 × 3 = 54§.',
    },
    {
      type: 'untimed',
      question: 'If a sequence starts at 9 and adds 4 each time, what is the 6th term?',
      options: ['25', '29', '33', '21'],
      correct: 1,
      explanation: '§a_6 = 9 + 5×4 = 29§.',
    },
    {
      type: 'untimed',
      question: 'Which sequence is geometric?',
      options: ['3, 6, 9, 12', '2, 4, 8, 16', '5, 8, 12, 17', '10, 8, 6, 3'],
      correct: 1,
      explanation: 'Each term is multiplied by the same number, 2.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      question: 'Find §1 + 4 + 7 + ... + 28§.',
      options: ['135', '140', '145', '150'],
      correct: 2,
      explanation:
        'There are 10 terms. Use §S = n(a_1+a_n)/2 = 10(1+28)/2 = 145§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'What is the 5th term of 3, 7, 11, ...?',
      options: ['15', '19', '23', '27'],
      correct: 1,
      explanation: '§d = 4§, so §a_5 = 3 + 4×4 = 19§.',
    },
    {
      type: 'timed',
      timeLimit: 95,
      question: 'A geometric sequence has §a_1 = 5§ and §r = 2§. What is §a_6§?',
      options: ['64', '80', '160', '320'],
      correct: 2,
      explanation: '§a_6 = 5 × 2^{5} = 5 × 32 = 160§.',
    },
  ];

  const quickReference = [
    'Nth term = the value at position n',
    'Arithmetic: §a_n = a_1 + (n-1)d§',
    'Geometric: §a_n = a_1r^{n-1}§',
    'Arithmetic series: §S_n = n(a_1+a_n)/2§',
    'Recursive rule: calculate terms one at a time',
    'First ask: adding, multiplying, or custom rule?',
  ];

  return (
    <LessonTemplate
      lessonId="sequences"
      lessonTitle="Sequences"
      lessonIcon={<FiList />}
      lessonDescription="Recognize arithmetic, geometric, recursive, and series patterns on GRE Quant"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={{
        title: 'Need help with GRE sequences?',
        spaceName: 'Quant hacks',
        humanCoachTitle: 'Talk to a GRE coach',
        liwazaAITitle: 'Ask AI',
        communityTitle: 'Ask other students',
        showHumanCoach: true,
        showLiwazaAI: true,
        showCommunity: true,
        aifirstmessage: 'I need help recognizing GRE Quant sequence patterns',
        aicontext:
          'Help with arithmetic sequences, geometric sequences, recursive rules, and series sums for GRE Quant.',
      }}
      supplementalPracticeSlot={<QuantLessonPracticeSlot lessonId="sequences" />}
    />
  );
};

export default SequencesLesson;
